import React, { FC } from 'react'
import { Controller, useWatch, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import { Button } from 'transfix-ui/components/Button'
import { ToastState } from 'transfix-ui/components/Toast'
import { Box, Typography } from 'transfix-ui/core'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import * as yup from 'yup'
import {
  FullPageOverlay,
  FullPageOverlayProps,
} from 'apps/fleet-planner/components/modals/full-page-overlay/FullPageOverlay'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { TrackingMethod } from 'apps/fleet-planner/pages/rate-confirmation/TrackingMethod'
import { ICarrier, IDriver, useUpdateShipmentTrackingMethod } from 'apps/fleet-planner/types'
import { fetchLoadDetails } from 'apps/fleet-planner/utils/actions/loadDetails'

export const TrackingMethodSchema = yup
  .object({
    trackingMethod: yup.string().required('Please select a tracking method'),
    driverPhone: yup.string().when('trackingMethod', {
      is: TRACKING_METHOD_TYPE.fmsMobile,
      then: yup
        .string()
        .required('Please enter cell phone number.')
        .min(10, 'Please enter a valid cell phone number.'),
      otherwise: yup.string().notRequired(),
    }),
  })
  .required()

type TrackingMethodSchemaType = yup.InferType<typeof TrackingMethodSchema>

interface ITrackingMethodModalProps extends Pick<FullPageOverlayProps, 'isOpen'> {
  trackingMethod?: TRACKING_METHOD_TYPE
  shipmentId?: string
  drivers?: IDriver[]
  carrier?: ICarrier
  onTrackingMethodUpdate: (newToastState: Omit<ToastState, 'toastOpen'>) => void
}

export const TrackingMethodModal: FC<ITrackingMethodModalProps> = ({
  carrier,
  drivers,
  isOpen,
  onTrackingMethodUpdate,
  shipmentId,
  trackingMethod,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const { isMobile } = useBreakpointContext()
  const [firstDriver] = drivers || []
  const { cellPhone } = firstDriver || {}
  const dispatch = useDispatch()
  const history = useHistory()
  const { pathname } = useLocation()
  const { loading: isLoading, mutate: updateUserData } = useUpdateShipmentTrackingMethod({
    id: shipmentId ? parseInt(shipmentId, 10) : 0,
  })

  const methods = useForm({
    resolver: yupResolver(TrackingMethodSchema),
    mode: 'all',
    shouldFocusError: true,
    defaultValues: TrackingMethodSchema.cast({
      trackingMethod: trackingMethod || '',
      driverPhone: cellPhone || '',
    }),
  })

  const { control, errors: formErrors, handleSubmit } = methods
  const values = useWatch({
    control,
    name: ['trackingMethod'],
  })

  const closeModalHandler = () => {
    history.push(pathname.replace(/(\/tracking-method)/, ''))
  }

  const submitHandler = async (data: TrackingMethodSchemaType) => {
    try {
      // Update tracking method.
      await updateUserData({
        trackingMethod: data?.trackingMethod,
        driverPhone: parseInt(data?.driverPhone, 10),
      })
      trackEvent('ELD', 'SUCCESSFULLY_CHANGED_TRACKING_METHOD')
      // Refetch load details to refresh data
      if (shipmentId) dispatch(fetchLoadDetails(shipmentId))
      onTrackingMethodUpdate({
        toastType: 'default',
        toastMessage: 'Tracking method updated',
      })
      // Close modal
      closeModalHandler()
    } catch (e) {
      onTrackingMethodUpdate({
        toastType: 'alert',
        toastMessage: 'Something went wrong, please contact us.',
      })
    }
  }

  const submitFormHandler = handleSubmit(submitHandler)

  return (
    <FullPageOverlay
      title='Tracking Method'
      isOpen={isOpen}
      onClose={closeModalHandler}
      showContentDividers={isMobile}
      fullScreen={isMobile}
      maxWidth='md'
      onSubmit={submitFormHandler}
      footer={
        <>
          <Button fullWidth={isMobile} color='tertiary' onClick={closeModalHandler} size='large'>
            Cancel
          </Button>
          <Button
            color='primary'
            disabled={isLoading}
            fullWidth={isMobile}
            size='large'
            onClick={submitFormHandler}>
            Save
          </Button>
        </>
      }>
      <Controller
        control={control}
        name='trackingMethod'
        render={({ onChange = () => {} }) => (
          <TrackingMethod
            defaultValue={trackingMethod}
            isProject44Connected={carrier?.isProject44Enabled || false}
            isKeepTruckinConnected={carrier?.isKeepTruckinEnabled || false}
            carrierId={carrier?.id}
            onChange={onChange}
            formErrors={formErrors}
          />
        )}
      />
      {values.trackingMethod === TRACKING_METHOD_TYPE.fmsMobile && (
        <Box>
          <Box mb={2}>
            <Typography color='textPrimary' variant='subtitle1'>
              Confirm your driver&apos;s phone number
            </Typography>
            <Box mt={0.5}>Your driver will use this phone number to log in to the app.</Box>
          </Box>
          <ControlledPhoneInput
            control={control}
            name='driverPhone'
            PhoneInputProps={{
              required: true,
              large: true,
              placeholder: '(555) 123-4567',
              label: `Driver's Cell Phone`,
              invalid: !!formErrors.driverPhone,
              validationMessage: formErrors?.driverPhone?.message,
              dataTest: 'drivers-phone-input',
              className: 'fs-mask',
            }}
          />
        </Box>
      )}
    </FullPageOverlay>
  )
}
